import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Loader2, Plus, CalendarDays, Clock, Video, Check, X, Settings2,
} from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import {
  API, fmtDate, APPT_TYPES, BOOKING_DAYS, BOOKING_TIME_OPTS, dayKey, myEmail,
} from "./shared";

const inputCls =
  "w-full bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]";

const PAY_METHODS = [
  ["none", "No payment"],
  ["cash", "Cash"],
  ["zelle", "Zelle"],
  ["check", "Check"],
  ["other", "Other"],
];

const EMPTY_FORM = {
  mode: "existing", client_email: "", guest_name: "", guest_email: "", guest_phone: "",
  staff_email: "", title: "", date: "", type: "consult", location: "",
  notes: "", key_info: "", is_video: false,
  payment_method: "none", amount: "", payment_status: "na",
};

const apptTime = (a) => {
  const d = a.date || "";
  if (d.includes("T")) return d.split("T")[1]?.slice(0, 5) || "";
  return (a.time || "").slice(0, 5);
};

export function ScheduleTab({ authHeader }) {
  const me = myEmail();
  const [team, setTeam] = useState([]);
  const [staff, setStaff] = useState("all");
  const [clients, setClients] = useState([]);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [dayData, setDayData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [avail, setAvail] = useState(null);
  const [editAvail, setEditAvail] = useState(false);
  const [availMsg, setAvailMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, a] = await Promise.all([
        axios.get(`${API}/admin/schedule/team`, { headers: authHeader() }),
        axios.get(`${API}/admin/clients`, { headers: authHeader() }),
        axios.get(`${API}/admin/appointments`, { headers: authHeader() }),
      ]);
      setTeam(t.data);
      setClients(c.data);
      setAppts(a.data);
      // default each employee to their own schedule
      if (me !== "owner" && t.data.some((m) => m.email === me)) setStaff(me);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // day view (appointments + availability + free slots) for the selected employee/day
  useEffect(() => {
    const d = dayKey(selectedDay);
    axios.get(`${API}/admin/schedule/day`, { headers: authHeader(), params: { date: d, staff } })
      .then((r) => setDayData(r.data))
      .catch(() => setDayData({ appointments: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, staff]);

  // availability editor state when an employee is selected
  useEffect(() => {
    setEditAvail(false);
    setAvailMsg("");
    if (staff === "all") { setAvail(null); return; }
    axios.get(`${API}/admin/schedule/availability/${encodeURIComponent(staff)}`, { headers: authHeader() })
      .then((r) => setAvail(r.data))
      .catch(() => setAvail(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff]);

  const staffAppts = useMemo(
    () => (staff === "all" ? appts : appts.filter((a) => (a.staff_email || "owner") === staff)),
    [appts, staff]
  );
  const markedDays = staffAppts.filter((a) => a.date).map((a) => new Date(a.date.slice(0, 10) + "T12:00:00"));
  const dayAppts = staffAppts
    .filter((a) => (a.date || "").slice(0, 10) === dayKey(selectedDay))
    .sort((a, b) => apptTime(a).localeCompare(apptTime(b)));

  const staffName = (email) => team.find((m) => m.email === email)?.name || email;

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMsg("");
    try {
      const body = {
        title: form.title, date: form.date, type: form.type, location: form.location,
        notes: form.notes, key_info: form.key_info, is_video: form.is_video,
        staff_email: form.staff_email || (staff === "all" ? me : staff),
        payment_method: form.payment_method,
        payment_status: form.payment_method === "none" ? "na" : form.payment_status,
      };
      if (form.mode === "existing") body.client_email = form.client_email;
      else {
        body.guest_name = form.guest_name;
        body.guest_email = form.guest_email;
        body.guest_phone = form.guest_phone;
      }
      if (form.amount !== "") body.amount = Number(form.amount);
      await axios.post(`${API}/admin/appointments`, body, { headers: authHeader() });
      setForm(EMPTY_FORM);
      setShowForm(false);
      setMsg("Appointment scheduled.");
      await load();
      setDayData(null);
      const d = dayKey(selectedDay);
      const r = await axios.get(`${API}/admin/schedule/day`, { headers: authHeader(), params: { date: d, staff } });
      setDayData(r.data);
    } catch (e2) {
      setMsg(e2?.response?.data?.detail || "Could not schedule the appointment.");
    } finally { setSaving(false); }
  };

  const markPaid = async (a) => {
    try {
      await axios.patch(`${API}/admin/appointments/${a.id}`, { payment_status: "paid" }, { headers: authHeader() });
      await load();
    } catch {}
  };

  const setAvailDay = (i, enabled, start, end) => {
    const weekly = { ...avail.weekly };
    weekly[String(i)] = enabled ? [[start, end]] : [];
    setAvail({ ...avail, weekly });
  };

  const saveAvail = async () => {
    setAvailMsg("");
    try {
      await axios.put(`${API}/admin/schedule/availability/${encodeURIComponent(staff)}`,
        { weekly: avail.weekly, slot_minutes: avail.slot_minutes }, { headers: authHeader() });
      setEditAvail(false);
      setMsg(`Availability saved for ${staffName(staff)}.`);
    } catch (e2) {
      setAvailMsg(e2?.response?.data?.detail || "Could not save availability.");
    }
  };

  const payBadge = (a) => {
    if (!a.payment_method || a.payment_method === "none") return null;
    const label = PAY_METHODS.find(([v]) => v === a.payment_method)?.[1] || a.payment_method;
    const amt = a.amount != null ? ` $${Number(a.amount).toFixed(0)}` : "";
    return a.payment_status === "paid" ? (
      <span className="text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full bg-[#899480]/15 text-[#3F5043]">
        Paid{amt} · {label}
      </span>
    ) : (
      <span className="text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#875F45]">
        Unpaid{amt} · {label}
      </span>
    );
  };

  return (
    <div data-testid="schedule-tab">
      <div className="mb-6 max-w-2xl">
        <div className="font-serif text-2xl text-[#3F5043] mb-1">Team Schedule</div>
        <p className="text-sm text-[#3F5043]/70 leading-relaxed">
          One calendar for the whole team — each employee keeps their own schedule and availability.
          Book existing clients or walk-ins, and record how they'll pay (cash, Zelle, check…).
        </p>
      </div>

      {/* employee picker */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="sched-staff-picker">
        {[{ email: "all", name: "All team" }, ...team].map((m) => (
          <button key={m.email} onClick={() => setStaff(m.email)}
            data-testid={`sched-staff-${m.email}`}
            className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.12em] font-semibold transition-colors ${
              staff === m.email
                ? "bg-[#3F5043] text-[#F8F7F3]"
                : "bg-white border border-[#BCB3A6]/50 text-[#3F5043] hover:bg-[#899480]/10"}`}>
            {m.name}{m.email === me ? " (you)" : ""}
          </button>
        ))}
      </div>

      {msg && <div className="mb-4 bg-[#899480]/10 border border-[#899480]/30 rounded-2xl p-3 text-sm text-[#3F5043]" data-testid="sched-msg">{msg}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* month calendar */}
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5" data-testid="sched-calendar">
          <Calendar
            mode="single"
            selected={selectedDay}
            onSelect={(d) => d && setSelectedDay(d)}
            modifiers={{ hasAppt: markedDays }}
            modifiersStyles={{ hasAppt: { fontWeight: 700, textDecoration: "underline", textDecorationColor: "#D4AF37", textUnderlineOffset: 4 } }}
          />
          <button onClick={() => { setShowForm(!showForm); setForm({ ...EMPTY_FORM, staff_email: staff === "all" ? me : staff }); }}
            data-testid="sched-new-open"
            className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors">
            <Plus size={13} /> Schedule appointment
          </button>
        </div>

        {/* day panel */}
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5" data-testid="sched-day-panel">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="font-serif text-lg text-[#3F5043] flex items-center gap-2">
              <CalendarDays size={16} className="text-[#899480]" />
              {selectedDay.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
            </div>
            {staff !== "all" && (
              <button onClick={() => setEditAvail(!editAvail)} data-testid="sched-avail-edit"
                className="inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                <Settings2 size={12} /> Availability
              </button>
            )}
          </div>

          {/* availability summary + free slots */}
          {staff !== "all" && dayData?.availability && (
            <div className="mb-4" data-testid="sched-availability">
              {dayData.availability.day_blocked ? (
                <div className="text-xs text-[#875F45]">This day is blocked on the booking calendar.</div>
              ) : dayData.availability.windows.length === 0 ? (
                <div className="text-xs text-[#899480]">{staffName(staff)} is not available on this weekday.</div>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
                    Open slots · {staffName(staff)}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {dayData.availability.free_slots.length === 0 && (
                      <span className="text-xs text-[#899480]">Fully booked.</span>
                    )}
                    {dayData.availability.free_slots.map((t) => (
                      <button key={t} data-testid={`sched-free-${t}`}
                        onClick={() => {
                          setShowForm(true);
                          setForm({ ...EMPTY_FORM, staff_email: staff, date: `${dayKey(selectedDay)}T${t}` });
                        }}
                        className="text-xs rounded-full px-3 py-1.5 bg-[#899480]/10 text-[#3F5043] hover:bg-[#3F5043] hover:text-[#F8F7F3] transition-colors">
                        {t}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* appointments for the day */}
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
            Appointments ({dayAppts.length})
          </div>
          {dayAppts.length === 0 ? (
            <div className="text-xs text-[#899480]" data-testid="sched-day-empty">Nothing scheduled this day.</div>
          ) : (
            <div className="space-y-2" data-testid="sched-day-list">
              {dayAppts.map((a) => (
                <div key={a.id} className="bg-[#F8F7F3] rounded-2xl px-4 py-3" data-testid={`sched-appt-${a.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-[#3F5043] flex items-center gap-1">
                      <Clock size={11} className="text-[#899480]" /> {apptTime(a) || "—"}
                    </span>
                    <span className="text-sm text-[#3F5043]">{a.title}</span>
                    {a.is_video && <Video size={12} className="text-[#875F45]" />}
                    <span className={`text-[9px] uppercase tracking-[0.12em] font-semibold px-2 py-0.5 rounded-full ${(APPT_TYPES[a.type] || APPT_TYPES.consult).cls}`}>
                      {(APPT_TYPES[a.type] || APPT_TYPES.consult).label}
                    </span>
                    {payBadge(a)}
                  </div>
                  <div className="text-xs text-[#899480] mt-1">
                    {a.guest_name || a.client_email}
                    {a.guest_name && a.client_email ? ` · ${a.client_email}` : ""}
                    {staff === "all" && a.staff_name ? ` · with ${a.staff_name}` : ""}
                    {a.status === "canceled" ? " · canceled" : ""}
                  </div>
                  {a.payment_method && a.payment_method !== "none" && a.payment_status === "unpaid" && (
                    <button onClick={() => markPaid(a)} data-testid={`sched-mark-paid-${a.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 border border-[#899480]/50 text-[#3F5043] hover:bg-[#899480]/10 transition-colors">
                      <Check size={12} /> Mark paid
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* availability editor */}
      {editAvail && avail && staff !== "all" && (
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 sm:p-6 mb-8" data-testid="sched-avail-editor">
          <div className="font-serif text-xl text-[#3F5043] mb-1">{staffName(staff)} — weekly availability (ET)</div>
          <p className="text-xs text-[#899480] mb-4">Team members can edit their own hours; the owner can edit anyone's.</p>
          <div className="space-y-2 mb-4">
            {BOOKING_DAYS.map((label, i) => {
              const win = (avail.weekly?.[String(i)] || [])[0] || null;
              return (
                <div key={label} className="flex flex-wrap items-center gap-3" data-testid={`sched-avail-day-${i}`}>
                  <label className="flex items-center gap-2 w-32">
                    <input type="checkbox" data-testid={`sched-avail-day-${i}-toggle`}
                      checked={!!win} onChange={(e) => setAvailDay(i, e.target.checked, "09:00", "17:00")}
                      className="accent-[#3F5043]" />
                    <span className="text-sm text-[#3F5043]">{label}</span>
                  </label>
                  {win ? (
                    <>
                      <select data-testid={`sched-avail-day-${i}-start`} value={win[0]}
                        onChange={(e) => setAvailDay(i, true, e.target.value, win[1])} className={inputCls}>
                        {BOOKING_TIME_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <span className="text-[#3F5043]/50 text-sm">to</span>
                      <select data-testid={`sched-avail-day-${i}-end`} value={win[1]}
                        onChange={(e) => setAvailDay(i, true, win[0], e.target.value)} className={inputCls}>
                        {BOOKING_TIME_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </>
                  ) : (
                    <span className="text-xs text-[#BCB3A6]">Off</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-[#3F5043]">Appointment length</label>
            <select data-testid="sched-slot-min" value={avail.slot_minutes}
              onChange={(e) => setAvail({ ...avail, slot_minutes: Number(e.target.value) })} className={inputCls}>
              {[30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
            <button onClick={saveAvail} data-testid="sched-avail-save"
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors">
              Save availability
            </button>
            {availMsg && <span className="text-xs text-[#875F45]" data-testid="sched-avail-msg">{availMsg}</span>}
          </div>
        </div>
      )}

      {/* new appointment form */}
      {showForm && (
        <form onSubmit={create} data-testid="sched-form"
          className="bg-white border border-[#D4AF37]/40 rounded-3xl p-5 sm:p-6 mb-8">
          <div className="font-serif text-xl text-[#3F5043] mb-4">Schedule appointment</div>

          {/* client: existing or new */}
          <div className="flex gap-2 mb-3">
            {[["existing", "Existing client"], ["new", "New client"]].map(([v, l]) => (
              <button type="button" key={v} onClick={() => setForm({ ...form, mode: v })}
                data-testid={`sched-mode-${v}`}
                className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.12em] font-semibold transition-colors ${
                  form.mode === v ? "bg-[#3F5043] text-[#F8F7F3]" : "bg-white border border-[#BCB3A6]/50 text-[#3F5043]"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            {form.mode === "existing" ? (
              <select required data-testid="sched-client" value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })} className={inputCls}>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id || c.email} value={c.email}>{c.full_name} ({c.email})</option>)}
              </select>
            ) : (
              <>
                <input required placeholder="Client name" value={form.guest_name} data-testid="sched-guest-name"
                  onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className={inputCls} />
                <input required type="email" placeholder="Client email" value={form.guest_email} data-testid="sched-guest-email"
                  onChange={(e) => setForm({ ...form, guest_email: e.target.value })} className={inputCls} />
                <input placeholder="Client phone" value={form.guest_phone} data-testid="sched-guest-phone"
                  onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className={inputCls} />
              </>
            )}
            <select data-testid="sched-assign" value={form.staff_email}
              onChange={(e) => setForm({ ...form, staff_email: e.target.value })} className={inputCls}>
              <option value="">Assign to…</option>
              {team.map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
            </select>
            <input required placeholder="Title (e.g. IVF consult — Dr. Smith)" value={form.title} data-testid="sched-title"
              onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <input required type="datetime-local" value={form.date} data-testid="sched-date"
              onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            <select value={form.type} data-testid="sched-type"
              onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
              {Object.entries(APPT_TYPES).map(([v, t]) => <option key={v} value={v}>{t.label}</option>)}
            </select>
            <input placeholder="Location or video link (optional)" value={form.location} data-testid="sched-location"
              onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} />
            <label className="flex items-center gap-2 text-sm text-[#3F5043]">
              <input type="checkbox" checked={form.is_video} data-testid="sched-video"
                onChange={(e) => setForm({ ...form, is_video: e.target.checked })} className="accent-[#3F5043]" />
              Video visit
            </label>
          </div>

          {/* payment — for clients paying another way */}
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Payment (if not paying online)</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <select value={form.payment_method} data-testid="sched-pay-method"
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className={inputCls}>
              {PAY_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            {form.payment_method !== "none" && (
              <>
                <input type="number" min="0" step="0.01" placeholder="Amount ($)" value={form.amount} data-testid="sched-pay-amount"
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
                <select value={form.payment_status} data-testid="sched-pay-status"
                  onChange={(e) => setForm({ ...form, payment_status: e.target.value })} className={inputCls}>
                  <option value="unpaid">Unpaid — collect later</option>
                  <option value="paid">Already paid</option>
                </select>
              </>
            )}
          </div>
          <textarea placeholder="Notes for the client (optional)" value={form.notes} rows={2} data-testid="sched-notes"
            onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-none mb-3`} />

          <div className="flex gap-3">
            <button type="submit" data-testid="sched-submit" disabled={saving}
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Schedule
            </button>
            <button type="button" onClick={() => setShowForm(false)} data-testid="sched-cancel"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#899480] hover:text-[#3F5043]">
              <X size={12} /> Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <div className="text-center py-6"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>}
    </div>
  );
}
